<script lang="ts">
  import { t } from '$lib/i18n';
  import { drawer } from '$lib/stores/drawer';
  import Drawer from '$components/drawer.svelte';
  import ReviewsForm from './reviews-form.svelte';
  import ReviewsPanel from './reviews-panel.svelte';
  import { createQuery } from '@tanstack/svelte-query';
  import { getReviewsData, type Reviews } from '$lib/yotpo';
  import { onMount } from 'svelte';
  import { page } from '$app/stores';
  import YotpoReviews from '$components/blocks/yotpo-reviews.svelte';
  import { cn } from '$lib/utils';
  import SliderButton from '$components/ui/slider-button.svelte';

  export let title: string | undefined = undefined;
  export let hideDesktop: boolean | undefined = undefined;
  export let hideMobile: boolean | undefined = undefined;
  export let product: {
    handle: string;
    id: string;
    title: string;
    category: string;
    price: number;
    currency: string;
    image: string;
    productType: string;
    variant: string;
  };

  let containerWidth = 0;
  let screenSize = 0;
  let scroll: HTMLDivElement;

  const SCROLL_BY = 300;

  $: scrollLeftMax = scroll?.scrollWidth;
  $: scrollLeft = 0;

  $: client = createQuery<Reviews>({
    staleTime: Infinity,
    queryKey: ['reviews', product.id],
    queryFn: async ({ pageParam = 1 }) => {
      return await getReviewsData({
        id: product.id,
        page: pageParam,
        star: undefined,
        sort: 'default'
      });
    }
  });

  $: totalReviews = $client.data?.bottomline?.total_review || 0;
  $: topReviews = $client.data?.reviews?.slice(0, 2);

  onMount(() => {
    if ($page.url.searchParams.get('review')) {
      setTimeout(() => drawer.open('review'));
      // remove param
      window.history.replaceState({}, '', window.location.pathname);
    }
  });
</script>

<svelte:window bind:innerWidth={screenSize} />
{#if totalReviews && topReviews && $client.data?.reviews}
  <section
    class={cn('px-2 lg:px-0 py-8', hideMobile && 'hidden lg:block', hideDesktop && 'lg:hidden')}
  >
    <div
      class={cn('container mx-auto px-2 lg:px-8 2xl:px-0 gap-4')}
      bind:clientWidth={containerWidth}
    >
      <div class={cn('flex mb-4', title ? 'justify-between' : 'justify-end')}>
        {#if title}
          <h2 class={cn('text-4xl font-semibold mb-4')}>
            {title}
          </h2>
        {/if}
        <div class="gap-2 px-2 lg:px-0 hidden lg:flex shrink-0 w-fit h-fit">
          <SliderButton
            icon="chevron-left"
            variant="ghost"
            disabled={scrollLeft === 0}
            class="shrink-0 w-fit"
            on:click={() => {
              scroll?.scrollBy({ left: -SCROLL_BY, behavior: 'smooth' });
            }}
          />
          <SliderButton
            icon="chevron-right"
            variant="ghost"
            class="shrink-0 w-fit"
            disabled={scrollLeftMax === scrollLeft}
            on:click={() => {
              scroll?.scrollBy({ left: SCROLL_BY, behavior: 'smooth' });
            }}
          />
        </div>
      </div>
    </div>

    <div
      class="gap-2 overflow-auto hide-scrollbar lg:gap-4 w-full flex"
      style:--review-margin={`${(screenSize - containerWidth) / 2 || 16}px`}
      bind:this={scroll}
      on:scroll={() => {
        scrollLeftMax = scroll?.scrollWidth - screenSize;
        scrollLeft = scroll.scrollLeft;
      }}
    >
      {#each $client.data.reviews.slice(0, 10) as review, i}
        <div
          class={cn(
            'shrink-0 rounded-md overflow-hidden',
            i === 0 && 'ml-0 lg:ml-8 2xl:ml-[--review-margin]',
            i === 9 && 'mr-0 lg:mr-8 2xl:mr-[--review-margin]'
          )}
        >
          <YotpoReviews
            reviews={[review]}
            variant="small"
            class="shrink-0"
            productId={product.id}
            productTitle={product.title}
            productType={product.productType}
            productVariant={product.variant}
            drawerIdPrefix={`review-section`}
          />
        </div>
      {/each}
    </div>
  </section>
{/if}

<!-- Drawer -->
<Drawer id={`review-section-${product.id}`} title={$t('reviews.title')}>
  <ReviewsPanel
    productId={product.id}
    productTitle={product.title}
    productType={product.productType}
    productVariant={product.variant}
  />
</Drawer>

<Drawer id="review" title={$t('reviews.post')}>
  <ReviewsForm {product} />
</Drawer>
